/**
 * Aufklapp-Panel unter einer Zeile der Admin-Benutzerliste.
 *
 * Zeigt pro User:
 * - Name bearbeiten (PATCH /api/admin/users/:id)
 * - Abteilungen + Spaces (direkt / über Abteilung geerbt) aus
 *   GET /api/admin/users/:id/access
 * - Bei Gästen: alle Einladungen (Projekt / Aufgabe, Berechtigung, Status)
 *   mit Aktionen „Link neu senden" (Zugang zurücksetzen) und „Widerrufen",
 *   sowie „Gast löschen".
 *
 * Es gibt keine Passwörter: interne Benutzer melden sich über Microsoft Entra
 * an (Reset dort), Gäste über Magic-Link + E-Mail-Code — deshalb ist der
 * Neuversand des Links das Gast-Äquivalent zum Passwort-Reset.
 */

import { useEffect, useState } from "preact/hooks";
import { toastError, toastSuccess } from "../../lib/toast";
import { confirmDialog } from "../../lib/confirm";
import { Modal } from "../Modal";
import {
  ApiError,
  deleteAdminUser,
  getAdminUserAccess,
  listAdminUsers,
  resendAdminInvite,
  revokeAdminInvite,
  updateAdminUser,
  type AdminGuestOpenTask,
  type AdminUser,
  type AdminUserAccess,
  type AdminUserAccessInvite,
} from "../../lib/api";

import { Button } from "../ui/Button";

interface UserAccessPanelProps {
  user: AdminUser;
  isSelf: boolean;
  onUserChanged: (updated: AdminUser) => void;
  onUserDeleted: (id: number) => void;
}

const STATUS_LABEL: Record<AdminUserAccessInvite["status"], string> = {
  active: "Aktiv",
  expired: "Abgelaufen",
  revoked: "Widerrufen",
};

const STATUS_CLS: Record<AdminUserAccessInvite["status"], string> = {
  active:
    "bg-[color:var(--color-primary-container)] text-[color:var(--color-on-primary-container)]",
  expired:
    "bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface-variant)]",
  revoked:
    "bg-[color:var(--color-error-container)] text-[color:var(--color-on-error-container)]",
};

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

function errMsg(err: unknown, fallback: string): string {
  if (err instanceof ApiError || err instanceof Error) return err.message;
  return fallback;
}

const H_CLS =
  "text-xs font-medium uppercase tracking-wide text-[color:var(--color-on-surface-variant)]";
const CHIP_CLS =
  "inline-block rounded px-1.5 py-0.5 text-micro bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface)]";
const BTN_CLS =
  "rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container)] disabled:opacity-40 disabled:cursor-not-allowed";

export function UserAccessPanel({
  user,
  isSelf,
  onUserChanged,
  onUserDeleted,
}: UserAccessPanelProps): preact.JSX.Element {
  const [access, setAccess] = useState<AdminUserAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState(user.name);
  const [savingName, setSavingName] = useState(false);
  const [busyInvites, setBusyInvites] = useState<Set<number>>(new Set());
  const [newLink, setNewLink] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [reassignOpenTasks, setReassignOpenTasks] = useState<AdminGuestOpenTask[] | null>(
    null
  );
  const [reassignCandidates, setReassignCandidates] = useState<AdminUser[]>([]);
  const [reassignTarget, setReassignTarget] = useState<number | "">("");

  useEffect(() => {
    void load();
    // Beim Wechsel des Users den Draft zurücksetzen.
    setNameDraft(user.name);
    setNewLink(null);
  }, [user.id]);

  async function load(): Promise<void> {
    setLoading(true);
    setLoadError(null);
    try {
      setAccess(await getAdminUserAccess(user.id));
    } catch (err) {
      setLoadError(errMsg(err, "Laden fehlgeschlagen"));
    } finally {
      setLoading(false);
    }
  }

  async function saveName(): Promise<void> {
    const next = nameDraft.trim();
    if (!next || next === user.name) return;
    setSavingName(true);
    try {
      const updated = await updateAdminUser(user.id, { name: next });
      onUserChanged(updated);
      toastSuccess("Name aktualisiert.");
    } catch (err) {
      toastError(errMsg(err, "Fehler beim Umbenennen"));
    } finally {
      setSavingName(false);
    }
  }

  function setInviteBusy(id: number, busy: boolean): void {
    setBusyInvites((prev) => {
      const s = new Set(prev);
      if (busy) s.add(id);
      else s.delete(id);
      return s;
    });
  }

  async function resend(inv: AdminUserAccessInvite): Promise<void> {
    setInviteBusy(inv.id, true);
    try {
      const result = await resendAdminInvite(inv.id);
      setNewLink(result.invite_url);
      toastSuccess(
        `Neuer Zugangslink an ${user.email} gesendet — der alte Link ist ungültig.`
      );
      await load();
    } catch (err) {
      toastError(errMsg(err, "Fehler beim Neuversand"));
    } finally {
      setInviteBusy(inv.id, false);
    }
  }

  async function revoke(inv: AdminUserAccessInvite): Promise<void> {
    const scope = inv.task_title
      ? `Aufgabe „${inv.task_title}"`
      : `Projekt „${inv.project_name ?? inv.project_id}"`;
    const ok = await confirmDialog({
      title: "Zugriff widerrufen?",
      message: `Der Zugriff auf ${scope} wird sofort entzogen.`,
      confirmLabel: "Widerrufen",
      danger: true,
    });
    if (!ok) return;
    setInviteBusy(inv.id, true);
    try {
      await revokeAdminInvite(inv.id);
      toastSuccess("Zugriff widerrufen.");
      await load();
    } catch (err) {
      toastError(errMsg(err, "Fehler beim Widerrufen"));
    } finally {
      setInviteBusy(inv.id, false);
    }
  }

  function applyDeleteResult(result: { deleted: boolean; deactivated: boolean }): void {
    if (result.deleted) {
      toastSuccess("Gast gelöscht.");
      onUserDeleted(user.id);
    } else {
      toastSuccess(
        "Gast deaktiviert und alle Zugänge widerrufen (der Account hat noch Inhalte, z. B. Kommentare)."
      );
      onUserChanged({ ...user, is_active: false });
      void load();
    }
  }

  async function removeGuest(): Promise<void> {
    const ok = await confirmDialog({
      title: "Gast löschen?",
      message:
        `„${user.name}" (${user.email}) wird gelöscht: alle Zugangslinks werden ` +
        "widerrufen und aktive Sitzungen beendet. Das kann nicht rückgängig gemacht werden.",
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const result = await deleteAdminUser(user.id);
      applyDeleteResult(result);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const openTasks = (err.body as { open_tasks?: AdminGuestOpenTask[] } | null)
          ?.open_tasks;
        if (openTasks && openTasks.length > 0) {
          setReassignTarget("");
          setReassignOpenTasks(openTasks);
          try {
            const all = await listAdminUsers();
            setReassignCandidates(
              all.filter((u) => !u.is_external && u.is_active && u.id !== user.id)
            );
          } catch (loadErr) {
            toastError(errMsg(loadErr, "Fehler beim Laden der Benutzerliste"));
          }
          return;
        }
      }
      toastError(errMsg(err, "Fehler beim Löschen"));
    } finally {
      setDeleting(false);
    }
  }

  async function confirmReassignAndDelete(): Promise<void> {
    if (!reassignTarget) return;
    setDeleting(true);
    try {
      const result = await deleteAdminUser(user.id, reassignTarget);
      setReassignOpenTasks(null);
      applyDeleteResult(result);
    } catch (err) {
      toastError(errMsg(err, "Fehler beim Löschen"));
    } finally {
      setDeleting(false);
    }
  }

  async function copyLink(): Promise<void> {
    if (!newLink) return;
    try {
      await navigator.clipboard.writeText(newLink);
      toastSuccess("Link kopiert.");
    } catch {
      toastError("Kopieren fehlgeschlagen — Link manuell markieren.");
    }
  }

  return (
    <>
    <div class="space-y-4 px-4 py-4 bg-[color:var(--color-surface-container-low)] border-t border-[color:var(--color-outline-variant)]">
      {/* Name bearbeiten */}
      <div class="flex flex-wrap items-end gap-2">
        <label class="block">
          <span class={H_CLS}>Name</span>
          <input
            type="text"
            value={nameDraft}
            onInput={(e) => setNameDraft((e.target as HTMLInputElement).value)}
            class="mt-1 block w-64 max-w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-base sm:text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            aria-label={`Name von ${user.email} bearbeiten`}
          />
        </label>
        <button
          type="button"
          onClick={() => void saveName()}
          disabled={savingName || !nameDraft.trim() || nameDraft.trim() === user.name}
          class={BTN_CLS}
        >
          {savingName ? "Speichert…" : "Umbenennen"}
        </button>
        {!user.is_external && (
          <span class="text-xs text-[color:var(--color-on-surface-variant)]">
            Anmeldung über Microsoft Entra — Passwort-Zurücksetzen erfolgt dort,
            nicht in juuplan.
          </span>
        )}
      </div>

      {loading ? (
        <p class="text-sm text-[color:var(--color-on-surface-variant)]">
          Lädt Zugriffs-Details…
        </p>
      ) : loadError ? (
        <div class="flex items-center gap-2 text-sm">
          <span class="text-[color:var(--color-error)]">Fehler: {loadError}</span>
          <button type="button" onClick={() => void load()} class={BTN_CLS}>
            Erneut laden
          </button>
        </div>
      ) : access ? (
        <>
          {/* Abteilungen + Spaces (für Gäste beides systembedingt leer) */}
          {!user.is_external && (
            <div class="grid gap-4 sm:grid-cols-2">
              <div>
                <p class={H_CLS}>Abteilungen</p>
                {access.departments.length === 0 ? (
                  <p class="mt-1 text-sm text-[color:var(--color-on-surface-variant)] italic">
                    Keine Abteilungen.
                  </p>
                ) : (
                  <div class="mt-1 flex flex-wrap gap-1">
                    {access.departments.map((d) => (
                      <span key={d.id} class={CHIP_CLS}>
                        {d.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p class={H_CLS}>Spaces</p>
                {access.spaces.length === 0 ? (
                  <p class="mt-1 text-sm text-[color:var(--color-on-surface-variant)] italic">
                    Kein Space-Zugriff über Mitgliedschaft oder Abteilung.
                  </p>
                ) : (
                  <ul class="mt-1 space-y-0.5 text-sm text-[color:var(--color-on-surface)]">
                    {access.spaces.map((s) => (
                      <li key={s.id} class="flex flex-wrap items-baseline gap-x-1.5">
                        <span class="font-medium">{s.name}</span>
                        <span class="text-xs text-[color:var(--color-on-surface-variant)]">
                          {s.role === "editor" ? "Bearbeiter" : "Betrachter"}
                          {" · "}
                          {s.via === "space"
                            ? "direktes Mitglied"
                            : `über Abteilung ${s.departments.join(", ")}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Gast-Zugriffe */}
          {(user.is_external || access.invites.length > 0) && (
            <div>
              <p class={H_CLS}>Gast-Zugriffe</p>
              {access.invites.length === 0 ? (
                <p class="mt-1 text-sm text-[color:var(--color-on-surface-variant)] italic">
                  Keine Einladungen.
                </p>
              ) : (
                <ul class="mt-1 divide-y divide-[color:var(--color-outline-variant)] rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]">
                  {access.invites.map((inv) => {
                    const busy = busyInvites.has(inv.id);
                    return (
                      <li
                        key={inv.id}
                        class="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
                      >
                        <div class="min-w-0 flex-1">
                          <span class="font-medium text-[color:var(--color-on-surface)]">
                            {inv.project_name ?? `Projekt #${inv.project_id}`}
                          </span>
                          {inv.task_id !== null && (
                            <span class="text-[color:var(--color-on-surface-variant)]">
                              {" "}
                              → Aufgabe „{inv.task_title ?? `#${inv.task_id}`}"
                            </span>
                          )}
                          <div class="text-xs text-[color:var(--color-on-surface-variant)]">
                            {inv.permission === "edit" ? "Bearbeiten" : "Nur lesen"}
                            {" · gültig bis "}
                            {fmtDate(inv.expires_at)}
                            {inv.accepted_at
                              ? ` · angenommen am ${fmtDate(inv.accepted_at)}`
                              : " · noch nicht angenommen"}
                          </div>
                        </div>
                        <span
                          class={`inline-block rounded px-1.5 py-0.5 text-label font-medium uppercase tracking-wide ${STATUS_CLS[inv.status]}`}
                        >
                          {STATUS_LABEL[inv.status]}
                        </span>
                        <div class="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => void resend(inv)}
                            disabled={busy}
                            class={BTN_CLS}
                            title="Zugang zurücksetzen: alter Link wird ungültig, neuer wird gemailt"
                          >
                            Link neu senden
                          </button>
                          {inv.status === "active" && (
                            <button
                              type="button"
                              onClick={() => void revoke(inv)}
                              disabled={busy}
                              class={`${BTN_CLS} text-[color:var(--color-error)]`}
                            >
                              Widerrufen
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {newLink && (
                <div class="mt-2 flex flex-wrap items-center gap-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2">
                  <span class="text-xs text-[color:var(--color-on-surface-variant)]">
                    Neuer Zugangslink (einmalig sichtbar):
                  </span>
                  <code class="min-w-0 flex-1 truncate text-xs text-[color:var(--color-on-surface)]">
                    {newLink}
                  </code>
                  <button type="button" onClick={() => void copyLink()} class={BTN_CLS}>
                    Kopieren
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Gast löschen */}
          {user.is_external && !isSelf && (
            <div class="pt-1">
              <button
                type="button"
                onClick={() => void removeGuest()}
                disabled={deleting}
                class="rounded border border-[color:var(--color-error)] px-2.5 py-1.5 text-xs font-medium text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] disabled:opacity-40"
              >
                {deleting ? "Löscht…" : "Gast löschen"}
              </button>
              <span class="ml-2 text-xs text-[color:var(--color-on-surface-variant)]">
                Widerruft alle Zugangslinks und beendet aktive Sitzungen.
              </span>
            </div>
          )}
        </>
      ) : null}
    </div>
    {reassignOpenTasks && (
      <Modal
        title="Offene Aufgaben übertragen"
        onClose={() => setReassignOpenTasks(null)}
        size="sm"
        footer={
          <div class="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setReassignOpenTasks(null)}
              class={BTN_CLS}
            >
              Abbrechen
            </button>
            <Button
              variant="destructive"
              size="md"
              type="button"
              onClick={() => void confirmReassignAndDelete()}
              disabled={!reassignTarget || deleting}
            >
              {deleting ? "Löscht…" : "Übertragen & löschen"}
            </Button>
          </div>
        }
      >
        <p class="text-sm text-[color:var(--color-on-surface)]">
          „{user.name}" ist noch{" "}
          {reassignOpenTasks.length === 1 ? "einer offenen Aufgabe" : "offenen Aufgaben"}{" "}
          zugewiesen. Wer übernimmt sie?
        </p>
        <ul class="mt-2 space-y-0.5 text-sm text-[color:var(--color-on-surface-variant)]">
          {reassignOpenTasks.map((t) => (
            <li key={t.id}>
              „{t.title}" ({t.project_name ?? `Projekt #${t.project_id}`})
            </li>
          ))}
        </ul>
        <label class="mt-3 block">
          <span class={H_CLS}>Neuer Verantwortlicher</span>
          <select
            value={reassignTarget === "" ? "" : String(reassignTarget)}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              setReassignTarget(v ? Number(v) : "");
            }}
            class="mt-1 block w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-base sm:text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
          >
            <option value="">Bitte wählen…</option>
            {reassignCandidates.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} — {u.email}
              </option>
            ))}
          </select>
        </label>
        <p class="mt-2 text-xs text-[color:var(--color-on-surface-variant)]">
          Bereits erledigte Aufgaben des Gastes gehen automatisch an ihre
          jeweiligen Ersteller zurück.
        </p>
      </Modal>
    )}
    </>
  );
}
